/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable promise/always-return */
import { BrowserApi } from '@/core/apis/browser_api';
import {
  TBrowserApiData,
  TEventInputData,
} from '@/core/constants/event_types/additional_types';
import {
  TEventMetaData,
  TEventPageData,
  TEventSpecificData,
  TMableEvent,
  UaHeaders,
} from '@/core/constants/event_types/base_mable_event';
import {
  EMableCustomEventName,
  EMableEventName,
} from '@/core/constants/event_types/events';
import {
  BrowserInstanceData,
  BrowserInstanceHandler,
} from '@/core/data_handlers/browser_instance';
import { ConsentHandler } from '@/core/data_handlers/consent';
import { TConsentMap } from '@/core/data_handlers/consent/types';
import { ICustomerDataFields } from '@/core/data_handlers/customer_data_new/types';
import { PageDataHandler } from '@/core/data_handlers/page_data/index';
import { ShopConfigDataHandler } from '@/core/data_handlers/shop_config_data';
import { ShopConfigHandler } from '@/core/data_handlers/shop_config_data/config_handler';
import {
  composeFinalMableEvent,
  validateCookies,
  validateCustomEventData,
} from '@/core/event_logic/final_event_composer';
import { composeGenericMableEvent } from '@/core/event_logic/generic_event_composer';
import { BotDetector } from '@/core/event_logic/utils/bot_detector';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { UniqueIdUtils } from '@/core/event_logic/utils/unique_id_utils';
import { isValidEventInputData } from '@/core/event_logic/utils/validate_event_input_data';
import { ScriptHandler } from '@/core/handlers/script';
import { EDestinationType } from '@/core/interfaces';
import EventWriterServiceApi from '@/destinations/bundles/ews/event_writer_service_api';
import { MetaCookieLogic } from '@/destinations/bundles/meta/cookie_logic';
import { Destinations } from '@/destinations/index';
import { normalizePhoneNumber } from '@/destinations/shared/phone_number_normalizer';
import {
  calculateCustomEvents,
  CustomEventDefinition,
} from '@/shared/fetch_client/custom_events';
import { Logger } from '@/shared/logger/logger';

import { ServiceMetricsUtils } from './utils/service_metrics_utils';

/**
 * Normalizes the event input data.
 * @param eventInputData The event input data to normalize
 * @returns The normalized event input data
 */
export function normalizeEventInputData(
  eventInputData: TEventInputData,
): TEventInputData {
  if (!eventInputData || Object.keys(eventInputData).length === 0) {
    return eventInputData;
  }
  const countryCode: string = eventInputData.cdas.map(cd => cd.cc)[0] || ''; // check for the first countryCode in all cdas
  eventInputData = {
    browserData: eventInputData?.browserData as TBrowserApiData,
    cdas: eventInputData?.cdas?.map(
      cd =>
        ({
          ...cd,
          ph: cd.ph ? normalizePhoneNumber(cd.ph, countryCode) : '',
          zip: cd.zip ? String(cd.zip) : '',
        }) satisfies ICustomerDataFields,
    ),
    eventData: eventInputData?.eventData,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  } satisfies TEventInputData;
  return eventInputData;
}

/**
 * Calculates triggered custom events based on the final Mable event.
 * @param shopConfig The shop configuration
 * @param finalMableEvent The final Mable event
 * @returns An array of triggered custom events
 */
export function calculateTriggeredCustomEvents(
  shopConfig: ShopConfigHandler,
  finalMableEvent: TMableEvent,
): TMableEvent[] {
  const customEventDefinitions: CustomEventDefinition[] =
    shopConfig.getCustomEventConfigs();
  const triggeredCustomEvents = calculateCustomEvents(
    finalMableEvent,
    customEventDefinitions,
  );
  return triggeredCustomEvents.map(customEventName => ({
    ...finalMableEvent,
    en: EMableEventName.CUSTOM_EVENT,
    esd: {
      ...finalMableEvent.esd,
      cen: customEventName as EMableCustomEventName,
    },
  }));
}

export type TMableEventInputData = {
  browserApiData: TBrowserApiData;
  config: ShopConfigHandler;
  destinationConsentMap: TConsentMap;
  metaData: TEventMetaData;
  pageData: TEventPageData;
  uaHeaders: UaHeaders;
  updatedBID: BrowserInstanceData;
};

/**
 * Calculates input data needed for event processing.
 * @param eventInputData The event input data
 * @param _eventName The event name
 * @returns The calculated Mable event input data
 */
export async function calculateInputData(
  eventInputData: TEventInputData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _eventName: EMableEventName,
): Promise<TMableEventInputData> {
  if (!eventInputData) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    eventInputData = {
      cdas: [],
      config: {},
    } as unknown as TEventInputData;
  }
  if (eventInputData && !eventInputData?.cdas) {
    eventInputData.cdas = [];
  }
  const config: ShopConfigHandler = await ShopConfigDataHandler.initOrLoad({});
  if (!config.shopConfigData?.active) {
    throw new Error(
      // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
      `Shop configuration is not active config: ${config.shopConfigData}`,
    );
  }
  eventInputData = normalizeEventInputData(eventInputData);

  // Initialize Meta cookies before getting browser data
  const timestamp = await TimeUtils.getCurrentEpochInMsSynced();

  const prevBrowserApiData: TBrowserApiData = await BrowserApi.get();
  const prevBrowserData: BrowserInstanceData =
    await BrowserInstanceHandler.loadAndMaybeUpdate(
      eventInputData?.cdas,
      prevBrowserApiData,
    );

  await MetaCookieLogic.initializeFbpIfNeeded(timestamp);
  await MetaCookieLogic.retrieveAndStoreFbclid(
    timestamp,
    prevBrowserData?.bd.ir,
  );

  if (eventInputData?.browserData) {
    eventInputData.browserData.cks = {
      ...eventInputData?.browserData?.cks,
      _fbp: MetaCookieLogic.getFbpCookie(),
      fbclid: MetaCookieLogic.getFbcCookie(),
    };
  }

  let pageData = PageDataHandler.get();
  if (eventInputData?.browserData) {
    pageData = PageDataHandler.setAfterPageLoad(
      BrowserApi.filterInvalidData(eventInputData?.browserData),
    );
  }

  pageData = PageDataHandler.incrementPageHitCount();
  const metaData: TEventMetaData = config.getMetaData();
  const destinationConsentMap: TConsentMap =
    ConsentHandler.getDestinationsConsentMap();
  const browserApiData: TBrowserApiData = await BrowserApi.get();
  const updatedBID: BrowserInstanceData =
    await BrowserInstanceHandler.loadAndMaybeUpdate(
      eventInputData?.cdas,
      browserApiData,
    );

  return {
    browserApiData,
    config,
    destinationConsentMap,
    metaData,
    pageData,
    uaHeaders: updatedBID.uaHeaders,
    updatedBID,
  };
}

/**
 * Transforms an event by adding necessary data and generating the final Mable event.
 * @param eventName The event name
 * @param eventInputData The event input data
 * @param inputData Additional input data needed for transformation
 * @returns The transformed Mable event
 */
export async function transformEvent(
  eventName: EMableEventName,
  eventInputData: TEventInputData,
  inputData: {
    browserApiData: TBrowserApiData;
    config: ShopConfigHandler;
    metaData: TEventMetaData;
    pageData: TEventPageData;
    uaHeaders: UaHeaders;
    updatedBID: BrowserInstanceData;
  },
): Promise<TMableEvent> {
  if (eventInputData?.eventData?.cpx_timestamp) {
    inputData.uaHeaders['x-cpx-timestamp'] =
      eventInputData.eventData.cpx_timestamp.toString();
    delete eventInputData.eventData.cpx_timestamp;
  }
  if (eventInputData?.eventData?.eventID) {
    inputData.uaHeaders['event-id'] = eventInputData.eventData.eventID;
    delete eventInputData.eventData.eventID;
  }
  const genericMableEvent: TMableEvent = await composeGenericMableEvent(
    eventName,
    inputData.updatedBID.sd,
    inputData.updatedBID.bd,
    inputData.updatedBID.cdas,
    inputData.updatedBID.pids,
    inputData.pageData,
    inputData.metaData,
    inputData.updatedBID.ii,
    inputData.uaHeaders,
  );
  genericMableEvent.bd.cks = validateCookies(
    genericMableEvent.bd.cks,
    genericMableEvent.sd.iqpar,
    genericMableEvent.ts,
    [EDestinationType.Facebook, EDestinationType.TikTok, EDestinationType.GA4],
  );
  const eventSpecificData: TEventSpecificData = validateCustomEventData(
    eventName,
    eventInputData?.eventData,
  );
  const finalMableEvent: TMableEvent = composeFinalMableEvent(
    eventName,
    eventSpecificData,
    genericMableEvent,
  );
  Logger.info('finalMableEvent', finalMableEvent);
  return finalMableEvent;
}

/**
 * Sends a wtag event to the Event Writer Service.
 * @param eventName The event name
 * @param eventInputData The event input data
 */
export async function sendWtagEventToEws(
  eventName: EMableEventName,
  eventInputData: TEventInputData,
): Promise<void> {
  try {
    const wtagPayload = {
      eventId: eventInputData?.eventData?.eventID || '',
      eventName,
      inputEventId: eventInputData?.eventData?.cpx_event_id || '',
      inputEventTimestamp:
        eventInputData?.eventData?.cpx_timestamp?.toString() || '',
      rawCpxPayload: eventInputData,
      sourceType: 'wtag',
      sourceUid: ScriptHandler.getInstance().getSourceUid(),
      wtagInput: eventInputData,
    };

    // Fire-and-forget event to EWS - don't block the main thread
    void EventWriterServiceApi.sendWtagEvent(wtagPayload as any)
      .then(() => {
        Logger.info('Successfully sent events to Event Writer Service', {
          eventName,
          wtagEventId: wtagPayload.eventId,
        });
      })
      .catch(error => {
        Logger.error('Failed to send events to Event Writer Service', error);
      });
  } catch (error) {
    Logger.error('Failed to handle event', error);
  }
}

/**
 * Ensures the eventInputData has required fields with default values if not provided.
 * - Adds an empty TEventInputData if not present
 * - Generates an eventID if not present
 * - Generates a timestamp if not present
 *
 * @param eventInputData The event input data to initialize
 * @returns The initialized event input data
 */
export async function initializeEventInputData(
  eventInputData: Partial<TEventInputData> | undefined,
): Promise<TEventInputData> {
  // If eventInputData is not present, create an empty one
  if (!eventInputData) {
    eventInputData = {
      cdas: [],
      eventData: {},
    };
  }

  // If eventData is not present, create an empty one
  if (!eventInputData.eventData) {
    eventInputData.eventData = {};
  }

  // Generate an eventID if not present
  if (!eventInputData.eventData.eventID) {
    eventInputData.eventData.eventID = UniqueIdUtils.getUuidV7();
  }

  // Generate a timestamp if not present
  if (!eventInputData.eventData.timestamp) {
    eventInputData.eventData.timestamp =
      await TimeUtils.getCurrentEpochInSecondsSynced();
  }

  return eventInputData as TEventInputData;
}

/**
 * Processes an event and sends it to all destinations.
 * @param eventName The event name
 * @param eventInputData The event input data
 * @returns A boolean indicating if the event was processed successfully
 */
export async function processEvent(
  eventName: EMableEventName,
  eventInputData: TEventInputData,
): Promise<boolean> {
  const startTime = await TimeUtils.getCurrentEpochInMsSynced();
  // Initialize event input data
  eventInputData = await initializeEventInputData(eventInputData);

  if (
    !isValidEventInputData(eventInputData) &&
    eventName !== EMableEventName.PAGE_VIEWED
  ) {
    Logger.warn('Invalid event input data', eventInputData, eventName);
  }
  // Send the wtag event to the Event Writer Service
  void sendWtagEventToEws(eventName, eventInputData);

  if (!eventName) {
    Logger.warn('Event name is required', eventInputData);
    return false;
  }

  // Check if this is a bot early to avoid unnecessary processing
  const userAgent = eventInputData?.browserData?.ua || navigator.userAgent;
  if (BotDetector.shouldSkipEventProcessing(userAgent)) {
    return false;
  }

  try {
    const inputData: TMableEventInputData = await calculateInputData(
      eventInputData,
      eventName,
    );

    let finalMableEvent = await transformEvent(
      eventName,
      eventInputData,
      inputData,
    );

    // Add wtag service metric to the final event at entry point
    finalMableEvent = await ServiceMetricsUtils.addServiceMetric(
      finalMableEvent,
      'wtag',
      startTime,
    );

    const allEvents = [
      finalMableEvent,
      ...calculateTriggeredCustomEvents(inputData.config, finalMableEvent),
    ];

    const results = await Promise.all(
      allEvents.map(event =>
        Destinations.processEvent(event, inputData.config),
      ),
    );

    return results.every(Boolean);
  } catch (error) {
    Logger.error('Error in handleEvent', error);
    return false;
  }
}
